import { FC } from 'react';
import GenericSection from 'components/commercetools-ui/organisms/content/cms-components-v2/generic-section';
import SectionWithProductListing from 'components/commercetools-ui/organisms/content/cms-components-v2/section-with-product-listing';
import SectionWithProductSlider from 'components/commercetools-ui/organisms/content/cms-components-v2/section-with-product-slider';
import OurStorySection from 'components/commercetools-ui/organisms/content/cms-components-v2/our-story-section';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import Link from 'components/commercetools-ui/atoms/link';
import { HomeIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { Tiempos } from 'fonts';

export type CmsMasterContainerV2Props = {
  data?: any;
};

const ComponentRegistry: { [key: string]: React.ElementType } = {
  generic_section: GenericSection,
  section_with_product_listing: SectionWithProductListing,
  section_with_product_slider: SectionWithProductSlider,
  our_story_section: OurStorySection,
  // add new content types here
};

const CmsMasterContainerV2: FC<CmsMasterContainerV2Props> = ({ data }) => {
  const renderComponents = () => {
    const componentList = [];
    const { sections = [] } = data ?? {};
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const sectionId = Object.keys(section)?.[0] ?? '';
      const Component = ComponentRegistry[sectionId];
      const dataToPass = section[sectionId];
      if (Component && dataToPass) {
        componentList.push(<Component key={i} content={dataToPass} />);
      }
    }
    return componentList;
  };

  const generateBreadcrumbs = () => {
    return (data?.breadcrumbs?.links ?? [])
    .filter((link: any) => link.title?.trim() && link.title?.trim().toLowerCase() != 'home')
    .map((link: any, index: number) => {
      const content = (
        <span key={index} className={`text-14 font-normal capitalize leading-4 text-[#666] ${Tiempos.className}`}>
          {link.title}
        </span>
      );
      return link.href ? <Link key={index} link={link.href}>{content}</Link> : content;
    });
  };

  return <>
          {data?.breadcrumbs?.links?.[0]?.title &&
            <div className="hidden px-24 pb-8 pt-16 md:block md:px-12 lg:px-32 xl:px-44 2xl:px-64">
            <Breadcrumb Separator={<ChevronRightIcon className="mt-2 w-16" />} listClassName="!gap-x-16">
              <Link link={'/'}>
                <HomeIcon className="mt-6 w-20" />
              </Link>
              {generateBreadcrumbs()}
            </Breadcrumb>
            </div>
          }

          {/*
            No row gap: each section carries its own margins (SECTION_SPACING), and a gap here would
            add to them rather than replace them.

            The outer margins are dropped at BOTH ends. Those margins are the space BETWEEN
            sections; outside the first and last there is no second section, only the header and the
            footer, which the live site sits flush against. Left in, they read as white bands.

            `style:first-child+*` is not belt-and-braces. A rendering with `additional_css` emits a
            <style> element as a SIBLING before its wrapper, so when the first section carries
            author CSS that <style> becomes the container's first child and `*:first-child` zeroes
            its margin instead of the section's -- leaving a white band under the header. Only the
            first rendering's <style> can ever sit there, so one extra selector covers it. The last
            end needs no such pair: a <style> always PRECEDES its own wrapper, so the final child is
            always a section.
          */}
          <div className="flex flex-col [&>*:first-child]:mt-0 [&>style:first-child+*]:mt-0 [&>*:last-child]:mb-0">
            {renderComponents()}
          </div>
        </>;
};

export default CmsMasterContainerV2;
